import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Pencil, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';

export default function PendingCredits() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({});

  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'credit_date', direction: 'desc' });

  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    entry_type: '',
    source: '',
  });

  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedPending, setSelectedPending] = useState(null);

  const { isAdmin, isEditor } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (overrideFilters) => {
    try {
      const activeFilters = overrideFilters || filters;
      const pendingParams = {};
      if (activeFilters.start_date) pendingParams.start_date = activeFilters.start_date;
      if (activeFilters.end_date) pendingParams.end_date = activeFilters.end_date;
      if (activeFilters.entry_type) pendingParams.entry_type = activeFilters.entry_type;
      if (activeFilters.source) pendingParams.source = activeFilters.source;

      const [pendingRes, optionsRes] = await Promise.all([
        api.get('/credit/pending', { params: pendingParams }),
        api.get('/credit/options'),
      ]);
      setPending(pendingRes.data.data || []);
      setOptions(optionsRes.data.data || {});
    } catch (error) {
      toast.error('Failed to load pending credits');
    } finally {
      setLoading(false);
    }
  };

  const requestSort = (key) => {
    setSortConfig((prev) => {
      const nextDirection = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction: nextDirection };
    });
  };

  const filteredPending = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let rows = Array.isArray(pending) ? [...pending] : [];

    if (q) {
      rows = rows.filter((c) => {
        const haystack = [c?.name, c?.entry_type, c?.source, c?.comments, c?.mobilenumber, c?.credit_date]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];

      if (key === 'amount') {
        const an = Number(av ?? 0);
        const bn = Number(bv ?? 0);
        return (an - bn) * dir;
      }

      if (key === 'credit_date') {
        const ad = av ? new Date(av).getTime() : 0;
        const bd = bv ? new Date(bv).getTime() : 0;
        return (ad - bd) * dir;
      }

      const as = String(av ?? '').toLowerCase();
      const bs = String(bv ?? '').toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });

    return rows;
  }, [pending, searchQuery, sortConfig]);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const openEditDrawer = (row) => {
    setSelectedPending(row);
    reset({
      credit_date: row?.credit_date ? String(row.credit_date).slice(0, 10) : '',
      amount: row?.amount ?? '',
      name: row?.name ?? '',
      entry_type: row?.entry_type ?? '',
      source: row?.source ?? '',
      mobilenumber: row?.mobilenumber ?? '',
      comments: row?.comments ?? '',
    });
    setShowDrawer(true);
  };

  const closeDrawer = () => {
    setShowDrawer(false);
    setSelectedPending(null);
    reset();
  };

  const onSubmit = async (data) => {
    if (!selectedPending?.id) return;
    try {
      await api.put(`/credit/pending/${selectedPending.id}`, data);
      toast.success('Pending credit updated');
      closeDrawer();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update pending credit');
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(`/credit/approve/${id}`);
      toast.success('Pending credit approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve credit');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pending credit?')) return;
    try {
      await api.delete(`/credit/pending/${id}`);
      toast.success('Pending credit deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete pending credit');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Pending Credits</h1>
            <p className="page-subtitle">Edit, approve, or delete promised credits before posting to master</p>
          </div>
          {(isAdmin || isEditor) && (
            <Link to="/credits" className="btn btn-outline">Back to Credits</Link>
          )}
        </div>
      </div>

      <div className="card">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <div>
            <label className="label">Start Date</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters((p) => ({ ...p, start_date: e.target.value }))}
              className="input"
            />
          </div>
          <div>
            <label className="label">End Date</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters((p) => ({ ...p, end_date: e.target.value }))}
              className="input"
            />
          </div>
          <div>
            <label className="label">Entry Type</label>
            <select
              value={filters.entry_type}
              onChange={(e) => setFilters((p) => ({ ...p, entry_type: e.target.value }))}
              className="input"
            >
              <option value="">All</option>
              {(options.entry_types || []).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Source</label>
            <input
              type="text"
              value={filters.source}
              onChange={(e) => setFilters((p) => ({ ...p, source: e.target.value }))}
              className="input"
              placeholder="Source contains..."
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4">
          <div className="text-sm text-gray-600">
            Total: <span className="font-medium">{pending.length}</span>
            {' '}| Showing: <span className="font-medium">{filteredPending.length}</span>
          </div>
          <div className="flex justify-end gap-3">
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => {
              const cleared = { start_date: '', end_date: '', entry_type: '', source: '' };
              setFilters(cleared);
              fetchData(cleared);
            }}
          >
            Clear
          </button>
          <button type="button" className="btn btn-primary" onClick={() => fetchData()}>
            Apply
          </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Pending List</h2>
          <div className="w-full sm:w-80">
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input"
              placeholder="Search pending credits..."
            />
          </div>
        </div>

        {filteredPending.length > 0 ? (
          <div className="table-container overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('credit_date')}>Date</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('name')}>Name</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('entry_type')}>Type</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('amount')}>Amount</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('source')}>Source</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('created_by')}>User</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPending.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDate(item.credit_date)}</td>
                    <td>{item.name}</td>
                    <td>{item.entry_type}</td>
                    <td className="font-semibold text-yellow-700">{formatCurrency(item.amount)}</td>
                    <td>{item.source}</td>
                    <td>{item.created_by || '-'}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        {isAdmin && (
                          <button
                            onClick={() => openEditDrawer(item)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => handleApprove(item.id)}
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center py-8 text-gray-500">No pending credits found</p>
        )}
      </div>

      {showDrawer && (
        <>
          <div className="drawer-overlay" onClick={closeDrawer} />
          <div className="drawer-panel">
            <div className="h-full flex flex-col">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Edit Pending Credit</h2>
                <button onClick={closeDrawer} className="p-2 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Date *</label>
                      <input
                        type="date"
                        {...register('credit_date', { required: 'Date is required' })}
                        className={`input ${errors.credit_date ? 'input-error' : ''}`}
                      />
                    </div>
                    <div>
                      <label className="label">Amount *</label>
                      <input
                        type="number"
                        step="0.01"
                        {...register('amount', { required: 'Amount is required', min: 0 })}
                        className={`input ${errors.amount ? 'input-error' : ''}`}
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">Name *</label>
                    <input
                      type="text"
                      {...register('name', { required: 'Name is required' })}
                      className={`input ${errors.name ? 'input-error' : ''}`}
                      placeholder="Enter name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Entry Type *</label>
                      <select {...register('entry_type', { required: 'Type is required' })} className="input">
                        <option value="">Select type</option>
                        {options.entry_types?.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                        <option value="Zakath">Zakath</option>
                        <option value="Sathaqa">Sathaqa</option>
                        <option value="General">General</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Source/Reference *</label>
                      <input
                        type="text"
                        {...register('source', { required: 'Source is required' })}
                        className={`input ${errors.source ? 'input-error' : ''}`}
                        placeholder="Enter source"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Mobile Number</label>
                      <input
                        type="tel"
                        {...register('mobilenumber')}
                        className="input"
                        placeholder="Enter mobile number"
                      />
                    </div>
                    <div>
                      <label className="label">Comments</label>
                      <textarea
                        {...register('comments')}
                        className="input"
                        rows="2"
                        placeholder="Add comments"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button type="button" onClick={closeDrawer} className="btn btn-outline">
                      Cancel
                    </button>
                    <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                      {isSubmitting ? 'Saving...' : 'Update Pending'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
